import axios from "axios";
import { API_BASE_URL } from "../constants";

/**
 * Fetch featured casinos by region
 * @param {string} regionId
 * @returns {Array} formatted casino list
 */
export const getFeaturedCasinosByRegion = async (regionId) => {
  try {
    const url = regionId
      ? `${API_BASE_URL}api/featured-casinos/get-featured-casinos?regionId=${regionId}`
      : `${API_BASE_URL}api/featured-casinos/get-featured-casinos`;

    const response = await axios.get(url);
    const data = response.data;

    if (!Array.isArray(data)) return [];

    const formatted = data
      .filter((item) => item.Casino?.isPublished)
      .map((item) => {
        const casino = item.Casino || {};

        const paymentMethods = (casino.PaymentMethods || [])
          .filter((pm) => pm.CasinoRegionPayment?.regionId === regionId)
          .sort(
            (a, b) =>
              (a.CasinoRegionPayment?.position || 0) -
              (b.CasinoRegionPayment?.position || 0)
          );

        const firstPM = paymentMethods[0]?.CasinoRegionPayment || {};

        return {
          id: casino.id,
          name: casino.name,
          imageUrl: casino.image,
          rating: casino.rating || "N/A",
          bestFor: casino.bestFor || "N/A",
          payoutSpeed: casino.payoutSpeed || "N/A",
          rtp: casino.rtp || "N/A",
          welcomeOffer:
            firstPM.welcomeOffer +
              " + " +
              (casino.freeSpins || "") +
              " Free Spins" || "N/A",
          minDeposit: firstPM.minDeposit || "N/A",
          casinoUrl: firstPM.casinoUrl || "#",
          promoCode: casino.promoCode || "N/A",
          paymentMethods: paymentMethods.map((pm) => ({
            id: pm.id,
            name: pm.name,
            imageUrl: pm.image,
          })),
        };
      });

    return formatted;
  } catch (error) {
    console.error("Error fetching featured casinos:", error);
    return [];
  }
};

export const getAllCasinos = async ({
  regionId,
  page = 1,
  bonusType = "all",
  sortBy,
}) => {
  try {
    const params = [];
    if (regionId) params.push(`regionId=${regionId}`);
    if (page) params.push(`page=${page}`);
    if (bonusType && bonusType !== "all") {
      params.push(`bonusType=${bonusType}`);
    }
    if (sortBy) {
      params.push(`sortBy=${sortBy}`);
    }

    const url = `${API_BASE_URL}api/casinos/getall${
      params.length ? "?" + params.join("&") : ""
    }`;

    const { data } = await axios.get(url);
    // If new response format (paginated)
    if (data && data.casinos && Array.isArray(data.casinos)) {
      const casinos = data.casinos
        .filter((casino) => casino.isPublished)
        .map((casino) => {
          const paymentMethods = (casino.PaymentMethods || [])
            .filter(
              (pm) => !regionId || pm.CasinoRegionPayment?.regionId === regionId
            )
            .sort((a, b) => {
              const posA = a.CasinoRegionPayment?.position || 0;
              const posB = b.CasinoRegionPayment?.position || 0;
              return posA - posB;
            });
          const firstPM = paymentMethods[0]?.CasinoRegionPayment || {};
          const welcomeOffer = casino?.freeSpins
            ? firstPM.welcomeOffer +
                " + " +
                (casino.freeSpins || "") +
                " Free Spins" || "N/A"
            : firstPM.welcomeOffer;
          return {
            id: casino.id,
            name: casino.name,
            imageUrl: casino.image,
            description: casino.description,
            rating: casino.rating || "N/A",
            bestFor: casino.bestFor || "N/A",
            payoutSpeed: casino.payoutSpeed || "N/A",
            rtp: casino.rtp || "N/A",
            promoCode: casino.promoCode || "N/A",
            freeSpins: casino.freeSpins || 0,
            welcomeOffer,
            minDeposit: firstPM.minDeposit || "N/A",
            casinoUrl: firstPM.casinoUrl || "#",
            regions: casino.Regions || [],
            categories: casino?.Categories || [],
            paymentMethods: paymentMethods.map((pm) => ({
              id: pm.id,
              name: pm.name,
              imageUrl: pm.image,
            })),
          };
        });
      return {
        casinos,
        total: data.total,
        page: data.page,
        limit: data.limit,
        totalPages: data.totalPages,
      };
    }

    // Fallback to old response (array)
    if (!Array.isArray(data))
      return { casinos: [], total: 0, page: 1, limit: 10, totalPages: 1 };

    const casinos = data
      .filter((casino) => casino.isPublished)
      .map((casino) => {
        const paymentMethods = (casino.PaymentMethods || [])
          .filter(
            (pm) => !regionId || pm.CasinoRegionPayment?.regionId === regionId
          )
          .sort((a, b) => {
            const posA = a.CasinoRegionPayment?.position || 0;
            const posB = b.CasinoRegionPayment?.position || 0;
            return posA - posB;
          });
        const firstPM = paymentMethods[0]?.CasinoRegionPayment || {};
        return {
          id: casino.id,
          name: casino.name,
          imageUrl: casino.image,
          description: casino.description,
          rating: casino.rating || "N/A",
          bestFor: casino.bestFor || "N/A",
          payoutSpeed: casino.payoutSpeed || "N/A",
          rtp: casino.rtp || "N/A",
          promoCode: casino.promoCode || "N/A",
          freeSpins: casino.freeSpins || 0,
          welcomeOffer:
            firstPM.welcomeOffer +
              " + " +
              (casino.freeSpins || "") +
              " Free Spins" || "N/A",
          minDeposit: firstPM.minDeposit || "N/A",
          casinoUrl: firstPM.casinoUrl || "#",
          regions: casino.Regions || [],
          categories: casino?.Categories || [],
          paymentMethods: paymentMethods.map((pm) => ({
            id: pm.id,
            name: pm.name,
            imageUrl: pm.image,
          })),
        };
      });

    return {
      casinos,
      total: casinos.length,
      page: 1,
      limit: casinos.length,
      totalPages: 1,
    };
  } catch (err) {
    console.error("Error fetching casinos:", err);
    return { casinos: [], total: 0, page: 1, limit: 10, totalPages: 1 };
  }
};

/**
 * Fetch a casino by ID
 * @param {string} casinoId - The UUID of the casino
 * @returns {Object|null} Casino details or null on failure
 */
export const getCasinoById = async (casinoId) => {
  try {
    if (!casinoId) throw new Error("Casino ID is required");

    const url = `${API_BASE_URL}api/casinos/get-casino/${casinoId}`;
    const response = await axios.get(url);

    if (response.status === 200) {
      return response.data;
    }

    console.error("Unexpected response status:", response.status);
    return null;
  } catch (error) {
    console.error("Error fetching casino by ID:", error);
    return null;
  }
};
/**
 * Fetch all casino feedbacks (testimonials)
 * @returns {Array} formatted feedback list
 */
export const getCasinoFeedbacks = async () => {
  try {
    const url = `${API_BASE_URL}api/datasettings/feedbacks`;

    const response = await axios.get(url);
    const data = response.data;

    if (!Array.isArray(data)) return [];

    const formatted = data
      .filter((item) => !item.deletedAt)
      .map((feedback) => ({
        id: feedback.id,
        name: feedback.name,
        review: feedback.review,
        rating: feedback.rating,
        imageUrl: feedback.image,
        createdAt: feedback.createdAt,
      }));

    return formatted;
  } catch (error) {
    console.error("Error fetching casino feedbacks:", error);
    return [];
  }
};

/**
 * Fetch all casino feedbacks (testimonials) using fetch API
 * @returns {Array} formatted feedback list
 */
export const getCasinoFeedbacksUsingFetch = async () => {
  try {
    const response = await fetch(`${API_BASE_URL}api/datasettings/feedbacks`, {
      method: "GET",
      redirect: "follow",
    });

    const text = await response.text();
    const data = JSON.parse(text);

    if (!Array.isArray(data)) return [];

    const formatted = data
      .filter((item) => !item.deletedAt)
      .map((feedback) => ({
        id: feedback.id,
        name: feedback.name,
        review: feedback.review,
        rating: feedback.rating,
        imageUrl: feedback.image,
        createdAt: feedback.createdAt,
      }));

    return formatted;
  } catch (error) {
    console.error("Error fetching casino feedbacks (via fetch):", error);
    return [];
  }
};

/**
 * Fetch all blogs using fetch API
 * @returns {Array} formatted blog list
 */
export const getAllBlogs = async () => {
  try {
    const response = await fetch(`${API_BASE_URL}api/blogs/blogs`, {
      method: "GET",
      redirect: "follow",
    });

    const text = await response.text();
    const data = JSON.parse(text);

    if (!Array.isArray(data)) return [];

    const formatted = data
      .filter((blog) => !blog.deletedAt)
      .map((blog) => ({
        id: blog.id,
        title: blog.title,
        slug: blog.slug,
        excerpt: blog.excerpt,
        content: blog.content,
        imageUrl: blog.image,
        publishedAt: blog.createdAt,
        author: blog.author || "Admin",
      }));

    return formatted;
  } catch (error) {
    console.error("Error fetching blogs:", error);
    return [];
  }
};

/**
 * Fetch a single blog by ID using fetch API
 * @param {string} blogId - UUID of the blog post
 * @returns {Object|null} blog data or null on error
 */
export const getBlogById = async (blogId) => {
  try {
    if (!blogId) throw new Error("Blog ID is required");

    const response = await fetch(
      `${API_BASE_URL}api/blogs/get-blog/${blogId}`,
      {
        method: "GET",
        redirect: "follow",
      }
    );

    const text = await response.text();
    const data = JSON.parse(text);

    if (!data || data.deletedAt) return null;

    return {
      id: data.id,
      title: data.title,
      slug: data.slug,
      excerpt: data.excerpt,
      content: data.content,
      imageUrl: data.image,
      publishedAt: data.createdAt,
      author: data.author || "Admin",
    };
  } catch (error) {
    console.error("Error fetching blog by ID:", error);
    return null;
  }
};

export const getAllCasinoContentListings = async (selectedRegionId = null) => {
  try {
    const url = selectedRegionId
      ? `${API_BASE_URL}api/casinos/content-listings?regionId=${selectedRegionId}`
      : `${API_BASE_URL}api/casinos/content-listings`;

    const { data } = await axios.get(url);

    return data;
  } catch (err) {
    console.error("Error fetching casino content listings:", err);

    return {
      forBeginners: [],
      largestGames: [],
      honorable: [],
      lowestDeposit: [],
    };
  }
};
